import { useRef, useEffect, useCallback, useMemo } from 'react'
import { select } from 'd3-selection'
import { zoom as d3Zoom, zoomIdentity } from 'd3-zoom'

// ── Constants ──

const NODE_HEIGHT = 28
const NODE_RX = 6
const FONT_SIZE = 10
const ARROW_SIZE = 8
const MAX_PER_ROW = 10
const GAP_X = 16
const GAP_Y_INNER = 12
const GAP_Y_LAYER = 80
const PADDING = 60
const LAYER_LABEL_WIDTH = 120
const NEIGHBORHOOD_HOPS = 2
const MAX_NEIGHBORHOOD = 60

// Layer greyscale (no student selected)
const LAYER_FILLS = {
  0: { light: '#d4d4d4', dark: '#525252' },
  1: { light: '#b8b8b8', dark: '#636363' },
  2: { light: '#9a9a9a', dark: '#787878' },
  3: { light: '#787878', dark: '#8f8f8f' },
  4: { light: '#525252', dark: '#a3a3a3' },
}

// Mastery colors: green / amber / red / gray
const MASTERY_FILLS = {
  mastered:   { light: '#16a34a', dark: '#22c55e' },
  developing: { light: '#d97706', dark: '#f59e0b' },
  weak:       { light: '#dc2626', dark: '#ef4444' },
  untested:   { light: '#e5e7eb', dark: '#374151' },
}

function getMasteryLevel(value) {
  if (value >= 0.8) return 'mastered'
  if (value >= 0.5) return 'developing'
  return 'weak'
}

function getNodeFill(node, masteryData, isDark) {
  const mode = isDark ? 'dark' : 'light'
  if (masteryData) {
    const nodeId = node.sat_node_id || node.id
    const mastery = masteryData[nodeId]
    const level = mastery ? getMasteryLevel(mastery.mastery_level) : 'untested'
    return MASTERY_FILLS[level][mode]
  }
  const layer = node.layer ?? 0
  return (LAYER_FILLS[layer] || LAYER_FILLS[0])[mode]
}

function getTextColor(fill) {
  const hex = fill.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#1a1a1a' : '#f5f5f5'
}

function getNodeId(concept) {
  return concept.sat_node_id || concept.id
}

// ── Label helpers ──

function buildLabelMap(concepts) {
  const labelCounts = new Map()
  for (const c of concepts) {
    const raw = c.subcategory || c.guideword || c.id
    labelCounts.set(raw, (labelCounts.get(raw) || 0) + 1)
  }

  const labels = new Map()
  for (const c of concepts) {
    const id = getNodeId(c)
    const raw = c.subcategory || c.guideword || c.id
    if (labelCounts.get(raw) > 1 && c.category) {
      labels.set(id, `${raw} (${c.category})`)
    } else {
      labels.set(id, raw)
    }
  }
  return labels
}

function truncateLabel(text, maxLen = 22) {
  if (!text || text.length <= maxLen) return text
  return text.slice(0, maxLen - 1) + '\u2026'
}

function measureTextWidth(text, fontSize = FONT_SIZE) {
  return text.length * fontSize * 0.55 + 16
}

// ── Neighborhood ──

function computeNeighborhood(focusId, concepts, edges, maxHops, maxNodes) {
  const visited = new Set([focusId])
  let frontier = new Set([focusId])

  for (let hop = 0; hop < maxHops; hop++) {
    const next = new Set()
    for (const nodeId of frontier) {
      for (const e of edges) {
        if (e.source === nodeId && !visited.has(e.target)) {
          visited.add(e.target)
          next.add(e.target)
        }
        if (e.target === nodeId && !visited.has(e.source)) {
          visited.add(e.source)
          next.add(e.source)
        }
      }
    }
    frontier = next
    if (visited.size >= maxNodes) break
  }
  return visited
}

// ── Static layout computation ──

function computeLayout(nodes, labelMap) {
  // Group by layer
  const layerGroups = {}
  for (const n of nodes) {
    const layer = n.layer ?? 0
    if (!layerGroups[layer]) layerGroups[layer] = []
    layerGroups[layer].push(n)
  }

  const layers = Object.keys(layerGroups).map(Number).sort((a, b) => a - b)
  const positioned = []

  // Compute each layer's height (accounting for sub-rows) bottom-to-top
  // Layer 0 at bottom = highest Y, Layer 4 at top = lowest Y
  const layerMeta = [] // { layer, y, height, nodeCount }

  // First pass: compute heights
  for (const layer of layers) {
    const group = layerGroups[layer]
    const subRowCount = Math.ceil(group.length / MAX_PER_ROW)
    const height = subRowCount * NODE_HEIGHT + (subRowCount - 1) * GAP_Y_INNER
    layerMeta.push({ layer, height, nodeCount: group.length, subRowCount })
  }

  // Second pass: assign Y positions (Layer 4 at top, Layer 0 at bottom)
  const reversedLayers = [...layerMeta].reverse() // start from highest layer
  let currentY = PADDING
  for (const meta of reversedLayers) {
    meta.y = currentY
    currentY += meta.height + GAP_Y_LAYER
  }

  const totalHeight = currentY - GAP_Y_LAYER + PADDING

  // Third pass: assign X positions within each layer
  let maxWidth = 0
  for (const meta of layerMeta) {
    const group = layerGroups[meta.layer]
    const nodesWithLabels = group.map(n => {
      const _id = getNodeId(n)
      const _label = labelMap.get(_id) || n.subcategory || n.guideword || n.id
      const _truncated = truncateLabel(_label)
      const _nodeWidth = measureTextWidth(_truncated)
      return { ...n, _id, _label, _truncated, _nodeWidth }
    })

    // Position nodes in sub-rows
    for (let i = 0; i < nodesWithLabels.length; i++) {
      const subRow = Math.floor(i / MAX_PER_ROW)
      const colIndex = i % MAX_PER_ROW
      const nodesInThisSubRow = Math.min(MAX_PER_ROW, nodesWithLabels.length - subRow * MAX_PER_ROW)

      // Compute x: use uniform cell width for alignment within each sub-row
      // Find max node width in this sub-row for uniform spacing
      const subRowStart = subRow * MAX_PER_ROW
      const subRowNodes = nodesWithLabels.slice(subRowStart, subRowStart + nodesInThisSubRow)
      const cellWidth = Math.max(...subRowNodes.map(n => n._nodeWidth)) + GAP_X

      const x = LAYER_LABEL_WIDTH + colIndex * cellWidth + cellWidth / 2
      const y = meta.y + subRow * (NODE_HEIGHT + GAP_Y_INNER) + NODE_HEIGHT / 2

      const rowWidth = LAYER_LABEL_WIDTH + nodesInThisSubRow * cellWidth
      if (rowWidth > maxWidth) maxWidth = rowWidth

      nodesWithLabels[i].x = x
      nodesWithLabels[i].y = y
    }

    positioned.push(...nodesWithLabels)
  }

  const totalWidth = maxWidth + PADDING

  return { nodes: positioned, layerMeta, totalWidth, totalHeight }
}

// ── Component ──

export default function GraphCanvas({
  concepts,
  edges,
  masteryData,
  selectedNode,
  highlightedPath,
  onSelectNode,
  onHoverNode,
  focusNodeId,
  onFocusNode,
}) {
  const containerRef = useRef(null)
  const svgRef = useRef(null)
  const tooltipRef = useRef(null)
  const nodeSelRef = useRef(null)
  const linkSelRef = useRef(null)
  const buildTimeoutRef = useRef(null)

  const isDark = typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')

  const onSelectNodeRef = useRef(onSelectNode)
  const onFocusNodeRef = useRef(onFocusNode)
  const onHoverNodeRef = useRef(onHoverNode)
  useEffect(() => { onSelectNodeRef.current = onSelectNode }, [onSelectNode])
  useEffect(() => { onFocusNodeRef.current = onFocusNode }, [onFocusNode])
  useEffect(() => { onHoverNodeRef.current = onHoverNode }, [onHoverNode])

  const labelMap = useMemo(() => buildLabelMap(concepts), [concepts])

  // Compute visible subset
  const { visibleConcepts, visibleEdges } = useMemo(() => {
    if (!concepts?.length) return { visibleConcepts: [], visibleEdges: [] }

    if (focusNodeId) {
      const neighborhood = computeNeighborhood(
        focusNodeId, concepts, edges, NEIGHBORHOOD_HOPS, MAX_NEIGHBORHOOD
      )
      const vc = concepts.filter(c => neighborhood.has(getNodeId(c)))
      const nodeIds = new Set(vc.map(c => getNodeId(c)))
      const ve = edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target))
      return { visibleConcepts: vc, visibleEdges: ve }
    }

    return { visibleConcepts: concepts, visibleEdges: edges }
  }, [concepts, edges, focusNodeId])

  // ── Build graph ──
  const buildGraph = useCallback(() => {
    const container = containerRef.current
    const svgEl = svgRef.current
    if (!container || !svgEl) return
    if (!visibleConcepts.length) return

    const viewWidth = container.clientWidth
    const viewHeight = container.clientHeight
    if (viewWidth === 0 || viewHeight === 0) return

    select(svgEl).selectAll('*').remove()

    const { nodes, layerMeta, totalWidth, totalHeight } = computeLayout(visibleConcepts, labelMap)
    const nodeMap = new Map(nodes.map(n => [n._id, n]))
    nodes.forEach(n => { if (n.id !== n._id) nodeMap.set(n.id, n) })

    const svg = select(svgEl).attr('width', viewWidth).attr('height', viewHeight)

    // Arrow marker
    const defs = svg.append('defs')
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 0 10 7')
      .attr('refX', 10).attr('refY', 3.5)
      .attr('markerWidth', ARROW_SIZE).attr('markerHeight', ARROW_SIZE)
      .attr('orient', 'auto')
      .append('polygon')
      .attr('points', '0 0, 10 3.5, 0 7')
      .attr('fill', isDark ? '#555' : '#999')

    // Highlighted arrow marker
    defs.append('marker')
      .attr('id', 'arrowhead-highlight')
      .attr('viewBox', '0 0 10 7')
      .attr('refX', 10).attr('refY', 3.5)
      .attr('markerWidth', ARROW_SIZE).attr('markerHeight', ARROW_SIZE)
      .attr('orient', 'auto')
      .append('polygon')
      .attr('points', '0 0, 10 3.5, 0 7')
      .attr('fill', isDark ? '#93c5fd' : '#2563eb')

    const g = svg.append('g')

    // Zoom
    const zoomBehavior = d3Zoom()
      .scaleExtent([0.2, 3])
      .on('zoom', (event) => g.attr('transform', event.transform))
    svg.call(zoomBehavior)

    // Layer labels and background bands
    for (const meta of layerMeta) {
      // Layer band background
      g.append('rect')
        .attr('x', 0)
        .attr('y', meta.y - NODE_HEIGHT / 2 - 8)
        .attr('width', totalWidth)
        .attr('height', meta.height + 16)
        .attr('rx', 8)
        .attr('fill', isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)')

      // Layer label
      g.append('text')
        .attr('x', 12)
        .attr('y', meta.y + (meta.height - NODE_HEIGHT) / 2 + NODE_HEIGHT / 2)
        .attr('text-anchor', 'start')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', 9)
        .attr('font-weight', 600)
        .attr('fill', isDark ? '#6b7280' : '#9ca3af')
        .text(`L${meta.layer}`)
    }

    // Edges
    const links = visibleEdges
      .map(e => {
        const source = nodeMap.get(e.source)
        const target = nodeMap.get(e.target)
        if (!source || !target) return null
        return { source, target, _key: `${e.source}->${e.target}` }
      })
      .filter(Boolean)

    const link = g.append('g').selectAll('line').data(links).join('line')
      .attr('x1', d => d.source.x)
      .attr('y1', d => d.source.y + NODE_HEIGHT / 2) // bottom of source (child)
      .attr('x2', d => d.target.x)
      .attr('y2', d => d.target.y - NODE_HEIGHT / 2 - 2) // top of target (prerequisite)
      .attr('stroke', isDark ? '#444' : '#d1d5db')
      .attr('stroke-width', 1)
      .attr('opacity', 0.4)
      .attr('marker-end', 'url(#arrowhead)')
    linkSelRef.current = link

    // Nodes
    const node = g.append('g').selectAll('g').data(nodes).join('g')
      .attr('transform', d => `translate(${d.x},${d.y})`)
      .attr('cursor', 'pointer')
    nodeSelRef.current = node

    // Rectangles
    node.append('rect')
      .attr('width', d => d._nodeWidth)
      .attr('height', NODE_HEIGHT)
      .attr('x', d => -d._nodeWidth / 2)
      .attr('y', -NODE_HEIGHT / 2)
      .attr('rx', NODE_RX)
      .attr('fill', d => getNodeFill(d, masteryData, isDark))
      .attr('stroke', 'transparent')
      .attr('stroke-width', 1)

    // Labels
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('font-size', FONT_SIZE)
      .attr('fill', d => getTextColor(getNodeFill(d, masteryData, isDark)))
      .text(d => d._truncated)

    // Click = select, double-click = focus
    node.on('click', (event, d) => {
      event.stopPropagation()
      onSelectNodeRef.current?.(d)
    })
    node.on('dblclick', (event, d) => {
      event.stopPropagation()
      onFocusNodeRef.current?.(d._id)
    })

    // Tooltip
    node.on('mouseenter', (event, d) => {
      const tooltip = tooltipRef.current
      if (tooltip) {
        tooltip.textContent = `${d._label} — L${d.layer ?? '?'} ${d.category || ''}`
        tooltip.style.display = 'block'
        tooltip.style.left = event.clientX + 12 + 'px'
        tooltip.style.top = event.clientY - 8 + 'px'
      }
      onHoverNodeRef.current?.(d)
    })
    node.on('mousemove', (event) => {
      const tooltip = tooltipRef.current
      if (tooltip) {
        tooltip.style.left = event.clientX + 12 + 'px'
        tooltip.style.top = event.clientY - 8 + 'px'
      }
    })
    node.on('mouseleave', () => {
      if (tooltipRef.current) tooltipRef.current.style.display = 'none'
      onHoverNodeRef.current?.(null)
    })

    // Background click = deselect
    svg.on('click', () => onSelectNodeRef.current?.(null))

    // Zoom to fit
    const contentWidth = totalWidth
    const contentHeight = totalHeight
    const scaleX = viewWidth / contentWidth
    const scaleY = viewHeight / contentHeight
    const scale = Math.min(scaleX, scaleY, 1.5) * 0.9
    const midX = contentWidth / 2
    const midY = contentHeight / 2
    const tx = viewWidth / 2 - midX * scale
    const ty = viewHeight / 2 - midY * scale
    svg.call(zoomBehavior.transform, zoomIdentity.translate(tx, ty).scale(scale))
  }, [visibleConcepts, visibleEdges, masteryData, labelMap, isDark])

  // ── Update selection visuals without rebuilding ──
  useEffect(() => {
    const nodeSel = nodeSelRef.current
    const linkSel = linkSelRef.current
    if (!nodeSel || !linkSel) return

    const selId = selectedNode ? (selectedNode.sat_node_id || selectedNode.id) : null

    // Edges connected to selected node
    const connectedEdges = new Set()
    const connectedNodes = new Set()
    if (selId) {
      connectedNodes.add(selId)
      linkSel.each(function (d) {
        const srcId = d.source._id || d.source
        const tgtId = d.target._id || d.target
        if (srcId === selId || tgtId === selId) {
          connectedEdges.add(d._key)
          connectedNodes.add(srcId)
          connectedNodes.add(tgtId)
        }
      })
    }

    // Highlighted path (trace weakness)
    const pathEdges = new Set()
    const pathNodes = new Set()
    if (highlightedPath) {
      highlightedPath.edges?.forEach(e => {
        pathEdges.add(`${e.source}->${e.target}`)
        pathNodes.add(e.source)
        pathNodes.add(e.target)
      })
    }

    const hasSelection = !!selId
    const hasPath = !!highlightedPath

    // Node styling
    nodeSel.select('rect')
      .attr('stroke', d => {
        if (d._id === selId) return isDark ? '#93c5fd' : '#2563eb'
        if (d._id === focusNodeId) return isDark ? '#a3a3a3' : '#666'
        if (masteryData) {
          const m = masteryData[d._id]
          if (!m) return isDark ? '#555' : '#d1d5db'
        }
        return 'transparent'
      })
      .attr('stroke-width', d => d._id === selId ? 2.5 : 1)
      .attr('stroke-dasharray', d => {
        if (d._id === selId) return null
        if (masteryData) {
          const m = masteryData[d._id]
          if (!m) return '3,2'
        }
        return null
      })

    nodeSel.attr('opacity', d => {
      if (hasPath) return pathNodes.has(d._id) ? 1 : 0.15
      if (hasSelection) return connectedNodes.has(d._id) ? 1 : 0.3
      return 1
    })

    // Edge styling
    linkSel
      .attr('stroke', d => {
        if (hasPath && pathEdges.has(d._key)) return isDark ? '#93c5fd' : '#2563eb'
        if (hasSelection && connectedEdges.has(d._key)) return isDark ? '#93c5fd' : '#2563eb'
        return isDark ? '#444' : '#d1d5db'
      })
      .attr('stroke-width', d => {
        if (hasPath && pathEdges.has(d._key)) return 2.5
        if (hasSelection && connectedEdges.has(d._key)) return 2
        return 1
      })
      .attr('opacity', d => {
        if (hasPath) return pathEdges.has(d._key) ? 1 : 0.05
        if (hasSelection) return connectedEdges.has(d._key) ? 0.8 : 0.08
        return 0.4
      })
      .attr('marker-end', d => {
        const highlighted = (hasPath && pathEdges.has(d._key)) ||
          (hasSelection && connectedEdges.has(d._key))
        return highlighted ? 'url(#arrowhead-highlight)' : 'url(#arrowhead)'
      })
  }, [selectedNode, highlightedPath, focusNodeId, masteryData, isDark])

  // Build on data change
  useEffect(() => {
    buildGraph()
  }, [buildGraph])

  // Debounced rebuild on resize
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      clearTimeout(buildTimeoutRef.current)
      buildTimeoutRef.current = setTimeout(buildGraph, 200)
    })
    if (containerRef.current) observer.observe(containerRef.current)
    return () => {
      observer.disconnect()
      clearTimeout(buildTimeoutRef.current)
    }
  }, [buildGraph])

  return (
    <div className="relative">
      <div className="flex items-center gap-2 mb-2 text-xs text-gray-500 dark:text-gray-400">
        <span>Showing {visibleConcepts.length} of {concepts.length} concepts</span>
        {focusNodeId && (
          <>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="font-medium text-gray-700 dark:text-gray-300">
              Focused: {labelMap.get(focusNodeId) || focusNodeId}
            </span>
            <button
              onClick={() => onFocusNode?.(null)}
              className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors"
            >
              Reset view
            </button>
          </>
        )}
      </div>

      <div ref={containerRef} className="relative w-full h-[600px] bg-gray-50 dark:bg-gray-950 rounded-lg border border-gray-200 dark:border-gray-800 overflow-auto">
        <svg ref={svgRef} className="w-full h-full" />
        <div
          ref={tooltipRef}
          className="fixed hidden z-50 px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-sm text-gray-700 dark:text-gray-300 pointer-events-none whitespace-nowrap"
        />
      </div>
    </div>
  )
}
